<template>
    <v-dialog
        v-model="show"
        max-width="560"
        persistent
    >
        <v-card>
            <v-card-title class="text-h6">
                {{ title }}
            </v-card-title>

            <v-card-text>
                <template v-if="phase === 'uploading'">
                    <p class="mb-2">
                        {{ $t('admin.users.importProgress.uploading') }}
                    </p>
                    <v-progress-linear
                        :value="uploadPercent"
                        :color="barColor"
                        height="24"
                        rounded
                    >
                        <strong class="white--text">{{ uploadPercent }} %</strong>
                    </v-progress-linear>
                </template>

                <template v-else-if="isReading">
                    <p class="mb-2">
                        {{ $t('admin.users.importProgress.reading') }}
                    </p>
                    <v-progress-linear
                        :color="barColor"
                        height="24"
                        rounded
                        indeterminate
                    />
                </template>

                <template v-else-if="isRunning">
                    <p class="mb-2">
                        {{ $t('admin.users.importProgress.running', { processed: number(status.processed), total: number(status.total) }) }}
                    </p>
                    <v-progress-linear
                        :value="status.percent"
                        :color="barColor"
                        height="24"
                        rounded
                    >
                        <strong class="white--text">{{ status.percent }} %</strong>
                    </v-progress-linear>
                    <p
                        v-if="eta"
                        class="mt-2 mb-0 text-caption"
                    >
                        {{ eta }}
                    </p>
                </template>

                <template v-else-if="isDone">
                    <v-progress-linear
                        :value="100"
                        color="success"
                        height="24"
                        rounded
                    >
                        <strong class="white--text">100 %</strong>
                    </v-progress-linear>
                    <p class="mt-3 mb-0">
                        {{ $t('admin.users.importSummary', { created: number(status.created), updated: number(status.updated), skipped: number(status.skipped) }) }}
                    </p>
                </template>

                <v-alert
                    v-else-if="isFailed"
                    type="error"
                    text
                    dense
                    class="mb-0"
                >
                    {{ failedMessage }}
                </v-alert>

                <p
                    v-if="active"
                    class="mt-4 mb-0 text-body-2"
                >
                    {{ $t('admin.users.importProgress.background') }}
                </p>
                <p
                    v-if="reconnecting"
                    class="mt-2 mb-0 text-caption"
                >
                    {{ $t('admin.users.importProgress.reconnecting') }}
                </p>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="secondary"
                    text
                    @click="show = false"
                >
                    {{ active ? $t('admin.users.importProgress.hide') : $t('common.close') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from '@/plugins/axios'

const ACTIVE = ['queued', 'reading', 'running'];
const POLL_MS = 2000;
const RETRY_MS = 5000;

/**
 * Sube el Excel de usuarios y sigue la importacion, que corre en el servidor
 * en segundo plano. El porcentaje es el real: filas que el servidor lleva
 * procesadas sobre las que tiene el Excel. No hay tiempo maximo: se espera lo
 * que tarde, y si se pierde la conexion se sigue preguntando.
 */
export default {
    name: 'UsersImportProgress',
    data() {
        return {
            show: false,
            phase: '', // 'uploading' | 'server'
            uploadPercent: 0,
            status: null,
            following: false,
            reconnecting: false,
            timer: null,
            barColor: '#0646ed'
        }
    },
    computed: {
        active() {
            return this.phase === 'uploading' || (!!this.status && ACTIVE.includes(this.status.status));
        },
        isRunning() {
            return this.status?.status === 'running' && !!this.status.total;
        },
        isReading() {
            return this.active && this.phase !== 'uploading' && !this.isRunning;
        },
        isDone() {
            return this.status?.status === 'done';
        },
        isFailed() {
            return this.status?.status === 'failed';
        },
        title() {
            if (this.isDone) return this.$t('admin.users.importProgress.doneTitle');
            if (this.isFailed) return this.$t('admin.users.importProgress.failedTitle');

            return this.$t('admin.users.importProgress.title');
        },
        failedMessage() {
            if (this.status?.error === 'interrupted') return this.$t('admin.users.importProgress.interrupted');

            return this.$t('admin.users.importError');
        },
        eta() {
            const seconds = this.status?.etaSeconds;

            if (seconds === null || seconds === undefined) return '';
            if (seconds < 60) return this.$t('admin.users.importProgress.etaUnderMinute');

            return this.$t('admin.users.importProgress.eta', { minutes: Math.ceil(seconds / 60) });
        }
    },
    async mounted() {
        // Si hay una importacion en marcha (lanzada antes de cerrar la pestana
        // o por otro administrador), se ensena como va.
        try {
            const response = await axios.get('usersFileImport/latest');
            const status = response?.data?.import;

            if (status && ACTIVE.includes(status.status)) {
                this.follow(status);
                this.show = true;
            }
        } catch (error) {
            // Sin estado no hay nada que retomar.
        }
    },
    beforeDestroy() {
        clearTimeout(this.timer);
    },
    methods: {
        number(value) {
            return Number(value ?? 0).toLocaleString(this.$i18n.locale === 'en' ? 'en-GB' : 'es-ES');
        },
        open() {
            this.show = true;
        },
        async start(file) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('async', '1');

            this.status = null;
            this.reconnecting = false;
            this.phase = 'uploading';
            this.uploadPercent = 0;
            this.show = true;
            this.emitProgress();

            try {
                const response = await axios.post('usersFileImport', formData, {
                    timeout: 0,
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (event) => {
                        if (event.total) {
                            this.uploadPercent = Math.min(100, Math.round(event.loaded * 100 / event.total));
                        }
                    }
                });

                this.follow(response.data.import);
            } catch (error) {
                const running = error?.response?.status === 409 ? error.response.data?.import : null;

                if (running) {
                    // Ya habia una en marcha: en vez de fallar, se ensena esa.
                    this.$store.dispatch('snackbar/showSnackbar', { color: 'info', message: this.$t('admin.users.importProgress.alreadyRunning') });
                    this.follow(running);

                    return;
                }

                this.phase = '';
                this.show = false;
                this.emitProgress();
                this.$store.dispatch('snackbar/error', error?.response?.data?.message ?? this.$t('admin.users.importError'));
            }
        },
        follow(status) {
            this.phase = 'server';
            this.status = status;
            this.following = true;
            this.emitProgress();
            this.schedule(POLL_MS);
        },
        schedule(delay) {
            clearTimeout(this.timer);
            this.timer = setTimeout(this.poll, delay);
        },
        async poll() {
            if (!this.status || !ACTIVE.includes(this.status.status)) return;

            try {
                const response = await axios.get('usersFileImport/' + this.status.id);

                this.status = response.data.import;
                this.reconnecting = false;
            } catch (error) {
                if (error?.response?.status === 404) {
                    this.status = Object.assign({}, this.status, { status: 'failed', error: 'interrupted' });
                } else {
                    // Red caida, 504 del gateway, reinicio...: se sigue intentando.
                    this.reconnecting = true;
                    this.schedule(RETRY_MS);

                    return;
                }
            }

            this.emitProgress();

            if (ACTIVE.includes(this.status.status)) {
                this.schedule(POLL_MS);
            } else if (this.following) {
                this.following = false;
                this.show = true;
                this.$emit('finished', this.status);
            }
        },
        emitProgress() {
            this.$emit('progress', {
                active: this.active,
                percent: this.phase === 'uploading' ? 0 : (this.status?.percent ?? 0)
            });
        }
    }
}
</script>
